import React, { useState, useEffect, useContext } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import { useQuery, useLazyQuery } from '@apollo/client';

import { Button, Modal } from '@superops/ui-design-system';
import {
  AppContext,
  ALL_SHARD_MODULES,
  DromoUploader,
  getTranslatedText
} from '@nile/components';
import { GET_ALL_FIELDS_V2 } from '@nile/banyan/src/Graphql/Mutations/ticket';
import Loader from '@nile/mangifera/src/Components/Ticket/TicketCount';
import { AccountClient } from '@nile/graphql';

import {
  getTechnicianImportFieldsUpdated,
  shapeOptions,
  getQuery,
  callQuery
} from '../Fields/technicianFields';
import {
  getValidators,
  getFieldType,
  getSelectOptions,
  intlConverter,
  isMultiSelect,
  splitter
} from '../utils/helpers';

const TechnicianUploader = ({
  onResults,
  onCancel,
  validationProps = {},
  fields: propFields,
  userToken,
  intl,
  activator
}) => {
  const appContext = useContext(AppContext);
  const { currentProduct } = appContext;
  const [results, setResults] = useState([]);
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [fields, setFields] = useState([]);
  const [clientCustomFields, setClientCustomFields] = useState([]);

  const toggleConfirmationActive = () => setConfirmationOpen(!confirmationOpen);

  const [
    getCustomFields,
    { loading: cfLoading, error: cfError, data: customFields }
  ] = useLazyQuery(GET_ALL_FIELDS_V2, {
    variables: { input: ALL_SHARD_MODULES.companyUser },
    client: AccountClient,
    fetchPolicy: 'no-cache',
    skip: currentProduct === 'MSP',
    onCompleted({ getAllFields }) {
      if (getAllFields) setClientCustomFields(getAllFields);
      const customFieldsToAdd = [];
      getAllFields?.forEach((cf) => {
        if (cf?.fieldCategory === 'CUSTOM') {
          customFieldsToAdd.push({
            label: cf.label,
            key: cf.columnName,
            type: getFieldType(cf),
            selectOptions: getSelectOptions(cf, true),
            validators: getValidators(cf)
          });
        }
      });
      setFields((fields) => [
        ...fields,
        ...intlConverter(intl, customFieldsToAdd)
      ]);
    }
  });

  const getTechnicianImportFields = getTechnicianImportFieldsUpdated(
    currentProduct
  );
  const multiselectFieldValidator = (keyName, values) => {
    const fieldValues = splitter(values);
    const associatedCustomField = clientCustomFields.find(
      ({ columnName }) => columnName === keyName
    );
    let isError = false;
    fieldValues.forEach((value) => {
      if (!isError && !associatedCustomField.options.includes(value))
        isError = true;
    });
    return isError;
  };

  const customFieldValidatorRowHook = ({ row, index }) => {
    const newRecord = { row, index };
    const multiSelectFields = clientCustomFields
      .filter((cf) => isMultiSelect(cf))
      .map(({ columnName }) => columnName);

    multiSelectFields.forEach((field) => {
      if (
        row[field]?.value?.trim() &&
        multiselectFieldValidator(field, row[field].value)
      ) {
        newRecord.row[field].info = [
          {
            message: getTranslatedText(
              intl,
              'dromo.custom.customFields.optionNotFound'
            ),
            level: 'error'
          }
        ];
      } else {
        if (newRecord?.row?.[field]) newRecord.row[field].info = [];
      }
    });

    return newRecord;
  };

  useEffect(() => {
    if (!propFields) {
      setLoading(true);
      let initFields = [...getTechnicianImportFields];

      const selectFields = getTechnicianImportFields.reduce(
        (acc, field, idx) => {
          if (field.type === 'select')
            acc[idx] = getQuery(field.key)
              ? callQuery(field.key)
              : field.selectOptions;
          return acc;
        },
        {}
      );

      const promises = Object.values(selectFields);
      Promise.all(promises)
        .then((options) => {
          options.forEach((set, idx) => {
            const indexToModify = Object.keys(selectFields)[idx];
            initFields[indexToModify].selectOptions = shapeOptions(
              initFields[indexToModify].key,
              set
            );
          });
          setFields((fields) => [
            ...intlConverter(intl, initFields),
            ...fields
          ]);
          if (currentProduct !== 'MSP') {
            getCustomFields();
          }
          setLoading(false);
        })
        .catch((e) => {
          setLoading(false);
          setError(true);
          console.log(e);
        });
    }
  }, []);

  // useEffect(() => {
  //   const { customFields: customFieldsFromProps } = validationProps;
  //   if (customFieldsFromProps) {
  //     setClientCustomFields(customFieldsFromProps);
  //   }
  // }, [validationProps]);

  if (loading || cfLoading) return <Loader loading />;

  if (error) return null;

  return (
    <div className="dromoBtnTrans">
      <Modal
        open={confirmationOpen}
        onClose={toggleConfirmationActive}
        variant="confirmation"
        primaryAction={{
          content: getTranslatedText(intl, 'dromo.confirmation.okay'),
          onAction: toggleConfirmationActive
        }}
        showCloseButton
        small
      >
        <Modal.Section>
          <FormattedMessage id="dromo.import.submit.notification" />
        </Modal.Section>
      </Modal>
      <DromoUploader
        settings={{
          importIdentifier: 'COMPANY_USER',
          title: getTranslatedText(intl, 'admin.technicians.import.title')
        }}
        fields={propFields || fields}
        rowHooks={[(row) => row, customFieldValidatorRowHook]}
        columnHooks={[(column) => column]}
        onResults={(response, metadata) => {
          setResults(response);
          toggleConfirmationActive();
          if (onResults) onResults();
        }}
        onCancel={() => {
          if (onCancel) onCancel();
        }}
        activator={
          activator || (
            <Button icon={<SVGIcon name="Import" />}>
              <FormattedMessage id="admin.employee.import" />
            </Button>
          )
        }
        userToken={userToken}
        backendSync
      />
    </div>
  );
};

export default injectIntl(TechnicianUploader);
